'use client';

import { Check, Compass, Flame, Footprints, Layers, Locate, Minus, Plus, RotateCcw, SlidersHorizontal, X, Zap } from 'lucide-react';
import { useState } from 'react';
import { INITIAL_FILTERS, type ConnectorType, type FilterState, type Charger, type HeatmapSubMode, type CpoFilterGroup, CPO_GROUPS } from '@/types/charger';

function operatorFilterLabel(
  value: string,
  operators: Array<{ organization_code: string; name: string }>,
): string {
  const names = value
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
    .map((code) => operators.find((op) => op.organization_code === code)?.name || code);
  if (names.length <= 1) return names[0] || value;
  return `${names[0]} és ${names.length - 1} további`;
}

export default function FilterBar({
  filters,
  onFilterChange,
  operators,
  mapStyle = 'vector',
  onMapStyleChange,
  onLocate,
  onZoomIn,
  onZoomOut,
  onResetNorth,
  selectedCharger,
  poiActive = false,
  onTogglePoi,
  heatmapSubMode = 'coverage',
  onHeatmapSubModeChange,
  cpoFilter = 'all',
  onCpoFilterChange,
  cpoCounts = {},
}: {
  filters: FilterState;
  onFilterChange: (f: FilterState) => void;
  operators: Array<{ organization_code: string; name: string }>;
  mapStyle?: 'vector' | 'satellite' | 'heatmap';
  onMapStyleChange?: (s: 'vector' | 'satellite' | 'heatmap') => void;
  onLocate?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetNorth?: () => void;
  selectedCharger?: Charger | null;
  poiActive?: boolean;
  onTogglePoi?: () => void;
  heatmapSubMode?: HeatmapSubMode;
  onHeatmapSubModeChange?: (m: HeatmapSubMode) => void;
  cpoFilter?: CpoFilterGroup;
  onCpoFilterChange?: (c: CpoFilterGroup) => void;
  cpoCounts?: Record<string, { count: number; evseTotal: number }>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const handleKw = (kw: number) =>
    onFilterChange({ ...filters, minKw: filters.minKw === kw ? 0 : kw });
  const toggleConnector = (c: ConnectorType) => {
    const connectors = filters.connectors.includes(c)
      ? filters.connectors.filter((x) => x !== c)
      : [...filters.connectors, c];
    onFilterChange({ ...filters, connectors });
  };
  const activeCount =
    (filters.minKw > 0 ? 1 : 0) +
    filters.connectors.length +
    (filters.onlyAvailable ? 1 : 0) +
    (filters.operator ? 1 : 0) +
    (filters.wcGas5Min ? 1 : 0);

  const chip = (on: boolean, extra = '') =>
    `px-3.5 py-1.5 rounded-full font-medium text-xs shadow-md whitespace-nowrap border backdrop-blur-md transition ${
      on ? extra || 'bg-emerald-600 text-white border-emerald-600' : 'bg-white/90 text-gray-700 border-gray-200 hover:bg-white'
    }`;

  // Uniform MapLibre control button schema (exact same style as GPS button)
  const ctrlBtn = (active = false, customActive = '') =>
    `relative flex h-[38px] w-[38px] items-center justify-center rounded-xl border transition-all active:scale-95 shadow-md ${
      active
        ? customActive || 'bg-slate-900 text-white border-slate-900 shadow-slate-900/20'
        : 'bg-white text-slate-700 border-slate-200/90 hover:bg-slate-50 hover:text-slate-900'
    }`;

  return (
    <>
      {/* 1. Desktop Filter / Heatmap Sub-Menu Bar (hidden on mobile) */}
      <div className="hidden md:block pointer-events-auto relative z-10 w-full px-4 py-1.5 md:px-6">
        {mapStyle === 'heatmap' ? (
          <div className="flex flex-col gap-1.5">
            <div className="no-scrollbar flex items-center gap-2 overflow-x-auto py-1">
              <span className="px-3 py-1.5 rounded-full bg-slate-900 text-amber-400 font-bold text-xs shadow-md border border-slate-800 flex items-center gap-1.5">
                <span>🔥</span>
                <span>Hőtérkép Típus:</span>
              </span>
              <button
                type="button"
                onClick={() => onHeatmapSubModeChange?.('coverage')}
                className={chip(heatmapSubMode === 'coverage', 'bg-gradient-to-r from-sky-600 to-emerald-600 text-white border-sky-600 shadow-md font-semibold')}
              >
                <span className="flex items-center gap-1.5">
                  <span>📶</span>
                  <span>1. Lefedettség & Hatósugár (DC 5km · AC 1km/500m)</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => onHeatmapSubModeChange?.('intelligence')}
                className={chip(heatmapSubMode === 'intelligence', 'bg-gradient-to-r from-amber-500 to-red-600 text-white border-amber-500 shadow-md font-semibold')}
              >
                <span className="flex items-center gap-1.5">
                  <span>⚡</span>
                  <span>2. Kihasználtság & Terheltség</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => onHeatmapSubModeChange?.('cpo')}
                className={chip(heatmapSubMode === 'cpo', 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-600 shadow-md font-semibold')}
              >
                <span className="flex items-center gap-1.5">
                  <span>🏢</span>
                  <span>3. CPO Piaci Megoszlás</span>
                </span>
              </button>
            </div>

            {/* CPO Selector Pills if SubMode is 'cpo' */}
            {heatmapSubMode === 'cpo' && (
              <div className="no-scrollbar flex items-center gap-1.5 overflow-x-auto py-1 animate-in fade-in slide-in-from-top-1">
                <span className="text-xs font-semibold text-slate-700 bg-white/90 px-2.5 py-1 rounded-full border shadow-sm">
                  CPO választó:
                </span>
                {CPO_GROUPS.map((cg) => {
                  const countInfo = cpoCounts[cg.id] || { count: 0, evseTotal: 0 };
                  const isSelected = cpoFilter === cg.id;
                  return (
                    <button
                      key={cg.id}
                      type="button"
                      onClick={() => onCpoFilterChange?.(cg.id)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-all shadow-sm ${
                        isSelected
                          ? `${cg.bgClass} text-white font-semibold ring-2 ring-white/60 scale-105`
                          : 'bg-white/95 text-slate-700 border border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cg.color }} />
                      <span>{cg.shortName}</span>
                      <span className="px-1.5 py-0.2 rounded-full bg-black/15 text-[10px] font-mono">
                        {countInfo.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="no-scrollbar flex items-center gap-2 overflow-x-auto py-1">
            <button type="button" onClick={() => setIsOpen(!isOpen)} className={chip(activeCount > 0)}>
              <span className="flex items-center gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Szűrők
                {activeCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold text-emerald-700">
                    {activeCount}
                  </span>
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onFilterChange({ ...filters, onlyAvailable: !filters.onlyAvailable })}
              className={chip(filters.onlyAvailable)}
            >
              ✅ Csak szabad
            </button>
            <button
              type="button"
              onClick={() => onFilterChange({ ...filters, wcGas5Min: !filters.wcGas5Min })}
              className={chip(Boolean(filters.wcGas5Min), "bg-blue-600 text-white border-blue-600 shadow-blue-600/30")}
              title="Olyan töltők, ahol 5 perces sétán belül (≤400m) elérhető mosdó / nyitva tartó benzinkút"
            >
              <span className="flex items-center gap-1">
                <span>🚻</span>
                <span>Mosdó & Nyitva lévő Kút (≤5p)</span>
              </span>
            </button>
            <button type="button" onClick={() => handleKw(22)} className={chip(filters.minKw === 22, 'bg-slate-800 text-white border-slate-800')}>
              AC 22 kW
            </button>
            <button type="button" onClick={() => handleKw(50)} className={chip(filters.minKw === 50, 'bg-amber-500 text-white border-amber-500')}>
              <span className="flex items-center gap-1">
                <Zap className="h-3 w-3 fill-current" /> 50+ kW
              </span>
            </button>
            <button type="button" onClick={() => handleKw(150)} className={chip(filters.minKw === 150, 'bg-indigo-600 text-white border-indigo-600')}>
              150+ kW
            </button>
          </div>
        )}
      </div>

      {/* 2. Mobile Floating Top Bar for Heatmap Sub-modes (visible only on mobile in heatmap mode) */}
      {mapStyle === 'heatmap' && (
        <div className="flex md:hidden pointer-events-auto w-full px-3 py-1 bg-slate-900/90 backdrop-blur-md border-b border-white/10 flex-col gap-1 z-30 shadow-lg animate-in fade-in slide-in-from-top-1">
          <div className="no-scrollbar flex items-center gap-1.5 overflow-x-auto py-0.5">
            <button
              type="button"
              onClick={() => onHeatmapSubModeChange?.('coverage')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs whitespace-nowrap transition-all ${
                heatmapSubMode === 'coverage'
                  ? 'bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-semibold shadow'
                  : 'text-slate-300 bg-slate-800/80 hover:text-white'
              }`}
            >
              <span>��</span>
              <span>Lefedettség</span>
            </button>
            <button
              type="button"
              onClick={() => onHeatmapSubModeChange?.('intelligence')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs whitespace-nowrap transition-all ${
                heatmapSubMode === 'intelligence'
                  ? 'bg-gradient-to-r from-amber-500 to-red-600 text-white font-semibold shadow'
                  : 'text-slate-300 bg-slate-800/80 hover:text-white'
              }`}
            >
              <span>⚡</span>
              <span>Kihasználtság</span>
            </button>
            <button
              type="button"
              onClick={() => onHeatmapSubModeChange?.('cpo')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs whitespace-nowrap transition-all ${
                heatmapSubMode === 'cpo'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow'
                  : 'text-slate-300 bg-slate-800/80 hover:text-white'
              }`}
            >
              <span>🏢</span>
              <span>CPO Megoszlás</span>
            </button>
          </div>

          {heatmapSubMode === 'cpo' && (
            <div className="no-scrollbar flex items-center gap-1 overflow-x-auto py-0.5 border-t border-white/10 pt-1">
              {CPO_GROUPS.map((cg) => {
                const countInfo = cpoCounts[cg.id] || { count: 0, evseTotal: 0 };
                const isSelected = cpoFilter === cg.id;
                return (
                  <button
                    key={cg.id}
                    type="button"
                    onClick={() => onCpoFilterChange?.(cg.id)}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] whitespace-nowrap transition-all ${
                      isSelected
                        ? `${cg.bgClass} text-white font-semibold ring-1 ring-white/50`
                        : 'bg-slate-800/70 text-slate-300 hover:text-white'
                    }`}
                  >
                    <span>{cg.shortName}</span>
                    <span className="px-1 py-0.2 rounded bg-black/30 text-[9px] font-mono">
                      {countInfo.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 2. Floating Action Column (Map Navigation & Mobile Quick Controls) */}
      <div className="pointer-events-auto fixed right-3 bottom-16 md:bottom-auto md:top-[9.5rem] md:right-4 z-20 flex flex-col items-center gap-1.5 max-h-[calc(100vh-5rem)] overflow-y-auto no-scrollbar py-1">
        {/* Séta & POI Toggle (visible when a charger is selected on mobile) */}
        {selectedCharger && (
          <button
            type="button"
            onClick={() => onTogglePoi?.()}
            className={`md:hidden ${ctrlBtn(poiActive, 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-600/30')}`}
            title="Közeli Séta & POI helyek"
            aria-label="Sétatávolság és környező helyek"
          >
            <Footprints className="h-4 w-4" />
          </button>
        )}

        {/* Mobile Layer Switcher Button (Desktop has segmented bar in EVMap) */}
        <button
          type="button"
          onClick={() =>
            onMapStyleChange?.(
              mapStyle === 'vector' ? 'satellite' : mapStyle === 'satellite' ? 'heatmap' : 'vector'
            )
          }
          className={`md:hidden ${ctrlBtn(
            mapStyle !== 'vector',
            mapStyle === 'heatmap'
              ? 'bg-slate-900 text-amber-400 border-slate-900 ring-2 ring-amber-400/40'
              : 'bg-slate-900 text-white border-slate-900'
          )}`}
          title={
            mapStyle === 'vector'
              ? 'Térkép nézet (Kattints a Műholdhoz)'
              : mapStyle === 'satellite'
              ? 'Műhold nézet (Kattints a Hőtérképhez)'
              : 'Lefedettségi Hőtérkép (Kattints a Térképhez)'
          }
          aria-label="Térképréteg váltása: Térkép, Műhold, Hőtérkép"
        >
          {mapStyle === 'heatmap' ? (
            <Flame className="h-4 w-4 fill-current text-amber-400 animate-pulse" />
          ) : (
            <Layers className="h-4 w-4" />
          )}
        </button>

        {/* Mobile Filter Modal Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={`md:hidden ${ctrlBtn(activeCount > 0, 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-600/30')}`}
          title="Szűrők megnyitása"
          aria-label="Szűrők beállítása"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {activeCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow ring-1 ring-white">
              {activeCount}
            </span>
          )}
        </button>

        {/* Mobile Quick Filter: Csak szabad */}
        <button
          type="button"
          onClick={() => onFilterChange({ ...filters, onlyAvailable: !filters.onlyAvailable })}
          className={`md:hidden ${ctrlBtn(filters.onlyAvailable, 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-600/30')}`}
          title="Csak szabad töltők"
          aria-label="Csak szabad töltők szűrése"
        >
          <span className={`flex h-3 w-3 rounded-full ${filters.onlyAvailable ? 'bg-white ring-2 ring-emerald-200' : 'bg-emerald-500 ring-1 ring-slate-300'}`} />
        </button>

        {/* Mobile Quick Filter: 50+ kW DC */}
        <button
          type="button"
          onClick={() => handleKw(50)}
          className={`md:hidden ${ctrlBtn(filters.minKw >= 50, 'bg-amber-500 text-white border-amber-500 shadow-amber-500/30')}`}
          title="50+ kW DC gyorstöltők"
          aria-label="50+ kW DC gyorstöltők szűrése"
        >
          <Zap className="h-4 w-4 fill-current" />
        </button>

        {/* Mobile Quick Filter: Mosdó / Nyitva lévő kút (≤5p) */}
        <button
          type="button"
          onClick={() => onFilterChange({ ...filters, wcGas5Min: !filters.wcGas5Min })}
          className={`md:hidden ${ctrlBtn(Boolean(filters.wcGas5Min), 'bg-blue-600 text-white border-blue-600 shadow-blue-600/30')}`}
          title="Mosdó & Nyitva lévő Kút (≤5 perc)"
          aria-label="Mosdó és benzinkút szűrés 5 perces sétán belül"
        >
          <span className="text-sm">🚻</span>
        </button>

        {/* Map Navigation Controls (Always visible on BOTH Mobile and Desktop) */}
        {/* Orientáció / Iránytű (Compass / Észak visszaállítása) */}
        <button
          type="button"
          onClick={() => onResetNorth?.()}
          className={ctrlBtn(false)}
          title="Észak és dőlésszög visszaállítása"
          aria-label="Észak és orientáció visszaállítása"
        >
          <Compass className="h-4 w-4 text-slate-700 hover:text-slate-900" />
        </button>

        {/* Térkép Nagyítás (Zoom In) */}
        <button
          type="button"
          onClick={() => onZoomIn?.()}
          className={ctrlBtn(false)}
          title="Nagyítás (+)"
          aria-label="Térkép nagyítása"
        >
          <Plus className="h-4 w-4 text-slate-700 hover:text-slate-900" />
        </button>

        {/* Térkép Kicsinyítés (Zoom Out) */}
        <button
          type="button"
          onClick={() => onZoomOut?.()}
          className={ctrlBtn(false)}
          title="Kicsinyítés (-)"
          aria-label="Térkép kicsinyítése"
        >
          <Minus className="h-4 w-4 text-slate-700 hover:text-slate-900" />
        </button>

        {/* GPS / Saját helyzetem Button */}
        <button
          type="button"
          onClick={() => onLocate?.()}
          className={ctrlBtn(false)}
          title="Saját pozíció meghatározása (GPS)"
          aria-label="Saját helyzet meghatározása"
        >
          <Locate className="h-4 w-4 text-slate-700 hover:text-slate-900" />
        </button>
      </div>

      {/* 3. Filter Modal Dialog */}
      {isOpen && (
        <div
          className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="pointer-events-auto relative z-10 w-full max-w-sm rounded-3xl border border-slate-100 bg-white p-5 text-sm shadow-2xl space-y-4 animate-in zoom-in-95 duration-150 text-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-emerald-600" />
                <h3 className="font-bold text-base text-slate-900">Szűrési feltételek</h3>
              </div>
              <div className="flex items-center gap-2">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={() => onFilterChange(INITIAL_FILTERS)}
                    className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition px-2 py-1 rounded-lg hover:bg-emerald-50"
                  >
                    <RotateCcw className="h-3 w-3" /> Alaphelyzet
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
                  title="Bezárás"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Teljesítmény */}
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                Minimális teljesítmény
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { kw: 0, label: 'Minden' },
                  { kw: 22, label: '22 kW' },
                  { kw: 50, label: '50 kW' },
                  { kw: 150, label: '150 kW+' },
                ].map((item) => {
                  const on = filters.minKw === item.kw;
                  return (
                    <button
                      key={item.kw}
                      type="button"
                      onClick={() => handleKw(item.kw)}
                      className={`rounded-xl border py-2 text-xs font-semibold transition ${
                        on
                          ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                          : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Csatlakozó */}
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                Csatlakozó típus
              </label>
              <div className="flex gap-2">
                {(['CCS', 'Type2', 'CHAdeMO'] as const).map((type) => {
                  const on = filters.connectors.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleConnector(type)}
                      className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2 text-xs font-semibold transition ${
                        on
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Gyorskapcsolók */}
            <div className="space-y-2">
              <label className="flex items-center justify-between cursor-pointer p-3 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition">
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <span className="flex h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-emerald-200" />
                  <span>Csak jelenleg szabad töltőpontok</span>
                </span>
                <input
                  type="checkbox"
                  checked={Boolean(filters.onlyAvailable)}
                  onChange={(e) => onFilterChange({ ...filters, onlyAvailable: e.target.checked })}
                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                />
              </label>

              <label className="flex items-center justify-between cursor-pointer p-3 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition">
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <span className="text-sm">🚻</span>
                  <span>Mosdó / Nyitva lévő kút (≤5p séta)</span>
                </span>
                <input
                  type="checkbox"
                  checked={Boolean(filters.wcGas5Min)}
                  onChange={(e) => onFilterChange({ ...filters, wcGas5Min: e.target.checked })}
                  className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
                />
              </label>
            </div>

            {/* Szolgáltató (CPO) */}
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                Szolgáltató (CPO hálózat)
              </label>
              <select
                value={filters.operator}
                onChange={(e) => onFilterChange({ ...filters, operator: e.target.value })}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Összes szolgáltató ({operators.length} db CPO)</option>
                {filters.operator && !operators.some((op) => op.organization_code === filters.operator) && (
                  <option value={filters.operator}>{operatorFilterLabel(filters.operator, operators)}</option>
                )}
                {operators.map((op) => (
                  <option key={op.organization_code} value={op.organization_code}>
                    {op.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full rounded-2xl bg-slate-900 py-3 text-xs font-bold text-white hover:bg-slate-800 transition shadow-lg shadow-slate-900/20"
            >
              Kész
            </button>
          </div>
        </div>
      )}
    </>
  );
}
